import { type RefObject, useEffect, useRef } from 'react';
import { panelSplitTabs } from '@/lib/desktop-shell/panel-split';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { matchesShortcut } from '@/lib/platform';
import { currentShortcut, NEXT_PANE_SHORTCUT } from '@/lib/settings-page/keyboard-shortcuts';

/** A menu, a dialog or a popover is open: what the user does there is not a pane change yet. */
function overlayOpen(): boolean {
  return (
    document.querySelector(
      '[role="dialog"][data-state="open"], [role="menu"][data-state="open"], [data-slot="popover-content"][data-state="open"], [data-composer-menu]',
    ) !== null
  );
}

/** The conversations of the split in their order on screen: the one in use and the others. */
function splitPaneOrder(state: ReturnType<DesktopShellStore['getState']>): string[] {
  const { chatId, splitThreadIds, conversationLayout } = state;
  return panelSplitTabs(conversationLayout).filter(
    (id) => id === chatId || splitThreadIds.includes(id),
  );
}

/** Where a press makes the other pane the one in use: its composer and its header. */
const ACTIVATING = '.desktop-composer, [data-testid="conversation-header"]';

/** How long after a key the focus moving counts as the keyboard's (Tab, ⌘L, ↵ on a link). */
const KEYBOARD_FOCUS_MS = 1000;

/**
 * In a split, the conversation in use follows the user (D56): pressing another pane's composer
 * or header, the keyboard's focus entering it (Tab, ⌘L) or ⌥⌘→ makes it the window's
 * conversation, so its ring, the sidebar's selection, the URL, the title, the floating composer
 * and the conversation's shortcuts are its own. Making it so draws both panes again (the window's
 * conversation is the shell's own pane), so a press in its transcript or one of its frames does
 * not: reading, scrolling, clicking a link or using an app there keeps the other one in use. A
 * press activates it once it is over (the click lands first), and not while a menu or a dialog it
 * opened is open: the focus coming back to the pane does.
 */
export function useSplitPaneFocus({
  store,
  containerRef,
  activate,
}: {
  store: DesktopShellStore;
  /** The workspace: panes elsewhere (a floating conversation, a library page) are not the split's. */
  containerRef: RefObject<HTMLElement | null>;
  activate: (threadId: string) => void;
}): void {
  const latest = useRef(activate);
  latest.current = activate;
  useEffect(() => {
    // The pane pressed, and whether the press makes it the one in use.
    let pressed: { id: string; activates: boolean } | null = null;
    let lastKeyAt = Number.NEGATIVE_INFINITY;
    let deferred: string | null = null;
    let timer: number | undefined;

    const otherPane = (id: string | null): boolean => {
      if (id === null) return false;
      const { chatId, splitThreadIds } = store.getState();
      return id !== chatId && splitThreadIds.includes(id);
    };
    const paneOf = (target: EventTarget | null): string | null => {
      const container = containerRef.current;
      if (!(target instanceof Element) || container === null || !container.contains(target))
        return null;
      const id = target.closest<HTMLElement>('[data-conversation-pane]')?.dataset[
        'conversationPane'
      ];
      return id !== undefined && otherPane(id) ? id : null;
    };
    const paneElement = (id: string) =>
      containerRef.current?.querySelector<HTMLElement>(`[data-conversation-pane="${id}"]`) ?? null;
    const selecting = (id: string): boolean => {
      const selection = window.getSelection();
      if (selection === null || selection.isCollapsed || selection.anchorNode === null)
        return false;
      return paneElement(id)?.contains(selection.anchorNode) ?? false;
    };
    const schedule = (id: string) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (!otherPane(id) || selecting(id)) return;
        // A menu or a dialog the press opened: the focus coming back to the pane activates it.
        if (overlayOpen()) {
          deferred = id;
          return;
        }
        deferred = null;
        latest.current(id);
      }, 0);
    };

    const onPointerDown = (event: PointerEvent) => {
      const id = paneOf(event.target);
      pressed =
        id === null
          ? null
          : { id, activates: (event.target as Element).closest(ACTIVATING) !== null };
    };
    // After the click it ends with: the pane is swapped in place once the press has landed.
    const onPointerUp = () => {
      const press = pressed;
      pressed = null;
      if (press?.activates) schedule(press.id);
    };
    const onPointerCancel = () => {
      pressed = null;
    };
    const onFocusIn = (event: FocusEvent) => {
      // A press gives the focus as it starts: its end decides. The focus moved by the app (a
      // dock taking the keys as it appears) is not the user's.
      if (pressed !== null) return;
      const id = paneOf(event.target);
      if (id === null) return;
      if (id === deferred || performance.now() - lastKeyAt <= KEYBOARD_FOCUS_MS) schedule(id);
    };
    const onAnyKey = () => {
      lastKeyAt = performance.now();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (!matchesShortcut(event, currentShortcut('next-pane') ?? NEXT_PANE_SHORTCUT)) return;
      const state = store.getState();
      const order = splitPaneOrder(state);
      if (order.length < 2) return;
      event.preventDefault();
      const next = order[(order.indexOf(state.chatId) + 1) % order.length];
      if (next !== undefined && next !== state.chatId) latest.current(next);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('pointerup', onPointerUp, true);
    window.addEventListener('pointercancel', onPointerCancel, true);
    document.addEventListener('focusin', onFocusIn);
    window.addEventListener('keydown', onAnyKey, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('pointerup', onPointerUp, true);
      window.removeEventListener('pointercancel', onPointerCancel, true);
      document.removeEventListener('focusin', onFocusIn);
      window.removeEventListener('keydown', onAnyKey, true);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [store, containerRef]);
}
